/**
 * useWorks — a lista de TRABALHOS da empresa ativa.
 *
 * Carrega as origens da empresa inteira e monta os trabalhos EM MEMÓRIA, com
 * `buildWorkList`. A alternativa (uma consulta por trabalho) seria um N+1 sobre
 * cinco tabelas; e, mais importante, a seleção passa pela MESMA função do
 * detalhe (`collectWorkSources`), então a lista e a tela do documento não podem
 * discordar sobre a fase de um trabalho.
 *
 * Três decisões que não são óbvias:
 *
 * 1. **Leitura PAGINADA e truncamento VISÍVEL.** Um `select` sem `.range()`
 *    devolve no máximo 1.000 linhas e o PostgREST não avisa que cortou
 *    (armadilha #50): a lista sairia incompleta em silêncio, e "não tenho mais
 *    nada em aberto" é exatamente a conclusão errada que o usuário tiraria.
 *    `truncated` sobe para a tela, que mostra o aviso.
 * 2. **As colunas são as MESMAS do detalhe** (importadas de `useWork`), pelo
 *    motivo acima: selecionar menos aqui mudaria a fase calculada.
 * 3. **Gate de PAPEL no financeiro.** `finance_entries` é owner/admin-only: para
 *    um `member` a consulta voltaria vazia e todo trabalho pago apareceria como
 *    em aberto. Não consultamos e passamos `null`, que o agregador traduz em
 *    "evidência limitada" em vez de "não recebido" (#38).
 *
 * Offline: monta a partir dos caches já aquecidos pelo `OfflineDataCacheWarmup`.
 * NFS-e não tem cache (módulo online-only), então offline a etapa fiscal não
 * aparece provada — nunca com informação errada.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import {
  CONTRACT_COLUMNS,
  FINANCE_COLUMNS,
  NFSE_COLUMNS,
  ORDER_COLUMNS,
  QUOTE_COLUMNS,
  RECEIPT_COLUMNS,
  useRefetchOnFinanceChange,
} from '@/hooks/useWork';
import {
  getCachedContracts,
  getCachedFinanceEntries,
  getCachedQuotes,
  getCachedReceipts,
  getCachedServiceOrders,
} from '@/lib/offlineDataCache';
import { fetchAllPages } from '@/lib/reportPaging';
import {
  buildWorkList,
  WORK_STAGE_ORDER,
  type WorkGroupLink,
  type WorkListEntry,
  type WorkRowPool,
  type WorkStageId,
} from '@/lib/work';

const WORKS_KEY = 'works';

interface WorksPayload {
  pool: WorkRowPool;
  /** Vínculos de agrupamento explícito — sem eles a lista discorda do detalhe. */
  links: WorkGroupLink[];
  truncated: boolean;
}

function poolFromCache(userId: string, canSeeFinance: boolean): WorkRowPool {
  return {
    quotes: getCachedQuotes(userId) ?? [],
    contracts: getCachedContracts(userId) ?? [],
    serviceOrders: getCachedServiceOrders(userId) ?? [],
    receipts: getCachedReceipts(userId) ?? [],
    nfseDocuments: [],
    financeEntries: canSeeFinance ? (getCachedFinanceEntries(userId) ?? []) : null,
  };
}

export interface UseWorksResult {
  entries: WorkListEntry[];
  /** `true` = a leitura bateu no teto de páginas; a lista está INCOMPLETA. */
  truncated: boolean;
  isLoading: boolean;
  refetch: () => void;
}

export function useWorks(): UseWorksResult {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canSeeFinance } = useRole();
  const contractsFeature = useFeature('contracts');
  const nfseFeature = useFeature('nfse_emission');

  const userId = user?.id ?? null;
  const companyId = activeCompany?.id ?? null;

  const suggestable: WorkStageId[] = WORK_STAGE_ORDER.filter((id) => {
    if (id === 'contract') return contractsFeature.allowed;
    if (id === 'nfse') return nfseFeature.allowed;
    return true;
  });

  const query = useQuery({
    queryKey: [WORKS_KEY, companyId, canSeeFinance],
    enabled: Boolean(userId && companyId),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    // Sem isto o `initialData` conta como fresco e a lista não revalidaria na
    // montagem — mostraria o estado anterior à própria ação do usuário (#11/#80).
    refetchOnMount: 'always',
    // `initialData` sem carimbo de tempo é tratado como FRESCO, e
    // `refetchOnMount` só é avaliado na MONTAGEM — mas esta query nasce
    // DESABILITADA (`userId` vem do AuthContext, que restaura a sessão de forma
    // assíncrona). Quando ela é habilitada, a montagem já passou e o dado
    // semeado do cache parece bom: nenhuma busca acontece. Resultado medido em
    // device: tudo que existe SÓ no servidor — o agrupamento explícito, um
    // documento criado em outro aparelho — ficava invisível até algo forçar um
    // refetch. Marcar o dado semeado como velho (época 0) é o que garante a
    // primeira busca.
    initialDataUpdatedAt: 0,
    initialData: (): WorksPayload | undefined =>
      userId
        ? { pool: poolFromCache(userId, canSeeFinance), links: [], truncated: false }
        : undefined,
    queryFn: async (): Promise<WorksPayload> => {
      if (!userId || !companyId) return { pool: {}, links: [], truncated: false };

      const paged = (table: string, columns: string) =>
        fetchAllPages<any>((from, to) => {
          const query: any = supabase
            .from(table as any)
            .select(columns)
            .eq('company_id', companyId);
          return query
            .order('created_at', { ascending: false })
            // Desempate estável: sem ele, duas linhas com o mesmo `created_at`
            // podem trocar de página e uma some da leitura (#93).
            .order('id', { ascending: false })
            .range(from, to);
        });

      /**
       * Sem rede o cliente do Supabase REJEITA (não devolve `{ error }`), e uma
       * rejeição dentro de `Promise.all` descarta as outras respostas — a lista
       * inteira sumiria por causa de uma tabela. Ver armadilha #58.
       */
      const safe = async (
        run: () => Promise<{ rows: any[]; truncated: boolean }>,
        fallback: any[],
      ): Promise<{ rows: any[]; truncated: boolean }> => {
        try {
          return await run();
        } catch {
          return { rows: fallback, truncated: false };
        }
      };

      const cached = poolFromCache(userId, canSeeFinance);
      const [quotes, contracts, serviceOrders, receipts, nfseDocuments, financeEntries] =
        await Promise.all([
          safe(() => paged('quotes', QUOTE_COLUMNS), cached.quotes ?? []),
          safe(() => paged('contracts', CONTRACT_COLUMNS), cached.contracts ?? []),
          safe(() => paged('service_orders', ORDER_COLUMNS), cached.serviceOrders ?? []),
          safe(() => paged('receipts', RECEIPT_COLUMNS), cached.receipts ?? []),
          safe(
            () =>
              fetchAllPages<any>((from, to) => {
                const query: any = supabase
                  .from('nfse_documents')
                  .select(NFSE_COLUMNS)
                  .eq('company_id', companyId)
                  .is('discarded_at', null);
                return query
                  .order('created_at', { ascending: false })
                  .order('id', { ascending: false })
                  .range(from, to);
              }),
            [],
          ),
          canSeeFinance
            ? safe(
                () => paged('finance_entries_with_status', FINANCE_COLUMNS),
                cached.financeEntries ?? [],
              )
            : Promise.resolve({ rows: [], truncated: false }),
        ]);

      // Sem rede o agrupamento simplesmente não se aplica: a lista derivada
      // continua correta, só não unida (mesma degradação do `useWork`).
      let links: WorkGroupLink[] = [];
      try {
        const { data } = await (supabase as any)
          .from('work_links')
          .select('work_key, document_kind, document_id')
          .eq('company_id', companyId);
        links = (data ?? []) as WorkGroupLink[];
      } catch {
        links = [];
      }

      return {
        links,
        pool: {
          quotes: quotes.rows,
          contracts: contracts.rows,
          serviceOrders: serviceOrders.rows,
          receipts: receipts.rows,
          nfseDocuments: nfseDocuments.rows,
          financeEntries: canSeeFinance ? financeEntries.rows : null,
        },
        truncated: [quotes, contracts, serviceOrders, receipts, nfseDocuments, financeEntries].some(
          (r) => r.truncated,
        ),
      };
    },
  });

  const payload = query.data;
  const entries = payload ? buildWorkList(payload.pool, { suggestable }, payload.links) : [];

  // Mesma razão do `useWork`: a fase de um trabalho muda quando um lançamento é
  // criado ou baixado em outra tela, e esta query não é invalidada por lá.
  useRefetchOnFinanceChange(() => void query.refetch());

  return {
    entries,
    truncated: payload?.truncated ?? false,
    isLoading: query.isLoading,
    refetch: () => void query.refetch(),
  };
}
